import {
  Badge,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';

import { Currency } from '@aslanpay/portal-common-lib';

import DeductionRequestsCategoryValues from 'app/constants/deductionRequests';
import { SavingsCycleCancelReasonLabels, SavingsCycleStateLabels } from 'app/constants/savings';

import { PlanEntryWithCycle } from './groupPlanEntries';
import { formatSavingAmount, formatSavingDate } from './savingFormatters';

interface SavingsCycleTableProps {
  title: string;
  entries: PlanEntryWithCycle[];
  currency: Currency;
}

const formatBalanceChange = (entry: PlanEntryWithCycle, currency: Currency) => {
  if (entry.preSaveBalance == null && entry.postSaveBalance == null) {
    return '-';
  }
  return `${formatSavingAmount(entry.preSaveBalance, currency)} → ${formatSavingAmount(
    entry.postSaveBalance,
    currency
  )}`;
};

const formatPoints = (entry: PlanEntryWithCycle) => {
  if (entry.points == null) {
    return '-';
  }
  if (entry.pointsStatus) {
    return `${entry.points} (${SavingsCycleStateLabels[entry.pointsStatus]})`;
  }
  return String(entry.points);
};

function SavingsCycleTable({ title, entries, currency }: SavingsCycleTableProps) {
  return (
    <TableContainer>
      <Text textStyle="smallBold" mb="8px">
        {`${title} (${entries.length})`}
      </Text>
      {entries.length ? (
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Payday</Th>
              <Th>Paid in</Th>
              <Th isNumeric>Amount</Th>
              <Th>Status</Th>
              <Th isNumeric>Balance</Th>
              <Th>Points</Th>
              <Th isNumeric>Points to date</Th>
              <Th>Request ID</Th>
            </Tr>
          </Thead>
          <Tbody>
            {entries.map((entry) => (
              <Tr key={entry.deductionRequestId || entry.savingPayCycleId}>
                <Td>{formatSavingDate(entry.payCycle?.payday)}</Td>
                <Td>{formatSavingDate(entry.paidInDate)}</Td>
                <Td isNumeric>{formatSavingAmount(entry.contributionAmount, currency)}</Td>
                <Td>
                  {entry.isProjection && <Badge colorScheme="badge.registered">Projected</Badge>}
                  {!entry.isProjection && entry.status && (
                    <Badge
                      colorScheme={`badge.${DeductionRequestsCategoryValues[entry.status]?.color}`}
                    >
                      {DeductionRequestsCategoryValues[entry.status]?.value}
                    </Badge>
                  )}
                  {entry.cancelReason && (
                    <Text textStyle="extraSmallRegular" color="brand.darkGray">
                      {SavingsCycleCancelReasonLabels[entry.cancelReason]}
                    </Text>
                  )}
                </Td>
                <Td isNumeric>{formatBalanceChange(entry, currency)}</Td>
                <Td>{formatPoints(entry)}</Td>
                <Td isNumeric>{entry.pointsToDate ?? '-'}</Td>
                <Td>
                  {entry.deductionRequestId ? (
                    <Tooltip hasArrow placement="top" label={entry.deductionRequestId}>
                      <Text as="span">{`${entry.deductionRequestId.slice(0, 8)}…`}</Text>
                    </Tooltip>
                  ) : (
                    '-'
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text textStyle="extraSmallRegular" color="brand.darkGray">
          {`No ${title.toLowerCase()} cycles`}
        </Text>
      )}
    </TableContainer>
  );
}

export default SavingsCycleTable;
